import { useRouter as useNextRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useNavigationState() {
  const { events } = useRouter();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const handleStart = (url: string) => {
      setUrl(url);
    };

    const handleEnd = () => {
      setUrl(null);
    };

    events.on('routeChangeComplete', handleEnd);
    events.on('routeChangeError', handleEnd);
    events.on('routeChangeStart', handleStart);

    return () => {
      events.off('routeChangeStart', handleStart);
      events.off('routeChangeError', handleEnd);
      events.off('routeChangeComplete', handleEnd);
    };
  }, [events]);

  return {
    url,
    isNavigating: url !== null,
  };
}

/**
 * A wrapper around the next router that prevents multiple pushes from happening at the same time.
 *
 * @returns
 */
export function useRouter() {
  const router = useNextRouter();
  const pushing = useRef(false);

  const push = useCallback(
    async (
      url: Parameters<typeof router.push>[0],
      as?: Parameters<typeof router.push>[1],
      options?: Parameters<typeof router.push>[2],
    ) => {
      if (pushing.current) {
        return false;
      }

      pushing.current = true;
      try {
        const result = await router.push.apply(router, [url, as, options]);
        pushing.current = false;

        return result;
      } catch (e) {
        pushing.current = false;
        throw e;
      }
    },
    [router],
  );

  return useMemo(
    () => ({
      ...router,
      push,
    }),
    [router, push],
  );
}
